import { useEffect, useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import { useResolveOabOffer } from '../../../services/oab/useResolveOabOffer';
import { OabResolveOfferResponse } from '../../../services/oab/types';
import {
  CAA_PR_BASE_ENTRY_COUPON,
  CAA_PR_DEGUSTACAO_COUPON,
} from '../caaPartnerOabCoupons';
import {
  trackOabPartnershipContinuedWithoutCoupon,
  trackOabPartnershipModalViewed,
  trackOabPartnershipNotFoundCorrectionAttempted,
  trackOabPartnershipResultViewed,
  trackOabPartnershipVerificationSubmitted,
} from '../trackOabPartnershipEvents';

export type OabPartnershipOutcome =
  | 'eligible'
  | 'range_mismatch'
  | 'no_special_plan'
  | 'not_found'
  | 'verify_unavailable';

export type OabPartnershipStep = 'input' | 'confirm' | OabPartnershipOutcome;

export type OabPartnershipNoticeTone = 'success' | 'warning' | 'info';

// Aviso que fica na página de cadastro (fora do modal, atrás dele) depois que o
// modal fecha — a pessoa já seguiu adiante e não veria mais a tela de resultado do modal.
export type OabPartnershipNotice = {
  tone: OabPartnershipNoticeTone;
  title: string;
  text: string;
};

const DEGUSTACAO_NOTICE: OabPartnershipNotice = {
  tone: 'info',
  title: 'Plano Degustação OAB-PR aplicado',
  text: 'Seguimos sem o cupom de parceria, mas o benefício Degustação OAB-PR está garantido no seu cadastro.',
};

const NO_SPECIAL_PLAN_NOTICE: OabPartnershipNotice = {
  tone: 'warning',
  title: 'Registro CAA-PR confirmado',
  text: 'Esse desconto é só pra faixas específicas de tempo de OAB, e a sua ficou fora delas. Mas como advogado CAA-PR, você ainda garante desconto exclusivo nos planos base.',
};

const ELIGIBLE_NOTICE: OabPartnershipNotice = {
  tone: 'success',
  title: 'Parceria CAA-PR confirmada',
  text: 'Desconto de parceria aplicado. Siga o cadastro para escolher a forma de pagamento.',
};

function rangeMismatchNotice(previousCoupon: string, resolvedCoupon?: string): OabPartnershipNotice {
  return {
    tone: 'success',
    title: 'Parceria CAA-PR confirmada',
    text: `O cupom ${previousCoupon} não corresponde ao seu tempo de OAB — aplicamos ${resolvedCoupon} automaticamente.`,
  };
}

function getInscriptionYear(inscriptionDate: string): string {
  return inscriptionDate.slice(0, 4);
}

// Desfechos "fechados" (mostram uma tela de resultado e não voltam pro
// formulário) fecham o modal sozinhos após esse intervalo — dá tempo da pessoa ler o aviso antes de
// seguir pro catálogo de planos já com o cupom trocado.
export const AUTO_CLOSE_DELAY_MS = 2500;

type UseOabPartnershipModalParams = {
  isOpen: boolean;
  urlCoupon: string;
  onApplyCoupon: (coupon: string) => void;
  onClose: () => void;
};

export function useOabPartnershipModal({
  isOpen,
  urlCoupon,
  onApplyCoupon,
  onClose,
}: UseOabPartnershipModalParams) {
  const [step, setStep] = useState<OabPartnershipStep>('input');
  const [oabNumber, setOabNumberState] = useState('');
  // Erro de campo obrigatório, distinto do desfecho "not_found" da API — evita mostrar "registro não
  // encontrado" (e contar `oab_partnership_result_viewed('not_found')`) pra quem nem chegou a
  // submeter um número de OAB (achado da review da JOR-380, CodeRabbit).
  const [fieldError, setFieldError] = useState<string | undefined>(undefined);
  const [pendingOffer, setPendingOffer] = useState<OabResolveOfferResponse | undefined>(
    undefined,
  );
  const [resolvedCoupon, setResolvedCoupon] = useState<string | undefined>(undefined);
  const [enteringCoupon, setEnteringCoupon] = useState(urlCoupon);
  const [notice, setNotice] = useState<OabPartnershipNotice | undefined>(undefined);
  const { mutateAsync, isLoading: isSubmitting } = useResolveOabOffer();
  // Uma verificação pendente pode responder depois que a pessoa já desistiu ("continuar sem
  // cupom"/X/backdrop-Esc na etapa inicial) — sem essa flag, o outcome tardio reaplicaria outro
  // cupom silenciosamente por cima do Degustação já aplicado no fechamento, com o modal já fechado.
  const hasExitedRef = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    hasExitedRef.current = false;
    // Congela o cupom que trouxe a pessoa até aqui: `onApplyCoupon` já reescreve a fonte de
    // `urlCoupon` (coupomCode/formik.values.coupom) antes do re-render que mostra a comparação de
    // "faixa diferente da esperada" — ler o prop ao vivo mostraria o mesmo cupom nos dois lados.
    setEnteringCoupon(urlCoupon);
    setNotice(undefined);
    setFieldError(undefined);
    trackOabPartnershipModalViewed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Desfechos com tela de resultado fechada (sem escolha do usuário) se
  // auto-fecham; "fora da faixa" é a única exceção — exige clique em "Continuar cadastro" (ver
  // onContinueRegistration) porque o catálogo de planos que aparece atrás precisa da atenção da
  // pessoa antes de seguir.
  useEffect(() => {
    const autoCloseSteps: OabPartnershipStep[] = [
      'eligible',
      'range_mismatch',
      'verify_unavailable',
    ];
    if (!autoCloseSteps.includes(step)) {
      return undefined;
    }
    const timer = setTimeout(() => {
      if (step === 'eligible') {
        setNotice(ELIGIBLE_NOTICE);
      } else if (step === 'range_mismatch') {
        setNotice(rangeMismatchNotice(enteringCoupon, resolvedCoupon));
      } else {
        setNotice(DEGUSTACAO_NOTICE);
      }
      onClose();
    }, AUTO_CLOSE_DELAY_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const applyVerifyUnavailableOutcome = () => {
    setStep('verify_unavailable');
    trackOabPartnershipResultViewed('verify_unavailable');
    onApplyCoupon(CAA_PR_DEGUSTACAO_COUPON);
  };

  const applyNotFoundOutcome = () => {
    setStep('not_found');
    trackOabPartnershipResultViewed('not_found');
  };

  const applyOutcome = (response: OabResolveOfferResponse) => {
    switch (response.offerStatus) {
      case 'eligible': {
        // Comparação case-insensitive: `isCaaPrSpecialCoupon` (que decide se o modal abre) já
        // trata os dois cupons como iguais independente de caixa — comparar case-sensitive aqui
        // mostraria "faixa diferente" pra um cupom que é, na prática, o mesmo.
        const outcome: OabPartnershipOutcome =
          response.eligibleCoupon?.toUpperCase() === enteringCoupon.toUpperCase()
            ? 'eligible'
            : 'range_mismatch';
        setResolvedCoupon(response.eligibleCoupon ?? undefined);
        setStep(outcome);
        trackOabPartnershipResultViewed(outcome);
        if (response.eligibleCoupon) {
          onApplyCoupon(response.eligibleCoupon);
        }
        return;
      }
      case 'no_special_plan':
        setStep('no_special_plan');
        trackOabPartnershipResultViewed('no_special_plan');
        onApplyCoupon(CAA_PR_BASE_ENTRY_COUPON);
        return;
      case 'not_found':
      case 'invalid_oab_number':
        applyNotFoundOutcome();
        return;
      case 'verify_unavailable':
      case 'state_not_supported':
      default:
        applyVerifyUnavailableOutcome();
    }
  };

  const setOabNumber = (value: string) => {
    setFieldError(undefined);
    setOabNumberState(value);
  };

  const onSubmit = async () => {
    if (!oabNumber.trim()) {
      setFieldError('Informe o número da OAB/PR para continuar.');
      return;
    }

    const isCorrection = step === 'not_found';
    if (isCorrection) {
      trackOabPartnershipNotFoundCorrectionAttempted();
    } else {
      trackOabPartnershipVerificationSubmitted();
    }

    try {
      const response = await mutateAsync({
        oab_state: 'PR',
        oab_number: oabNumber,
      });
      if (hasExitedRef.current) {
        return;
      }
      // Antes de aplicar qualquer desfecho, a pessoa confirma que o registro encontrado é o dela
      // (nome mascarado + inscrição) — só "não encontrado"/"indisponível" pulam direto pro resultado,
      // já que não há identidade nenhuma pra confirmar.
      if (response.found) {
        setPendingOffer(response);
        setStep('confirm');
        return;
      }
      applyOutcome(response);
    } catch (error) {
      if (hasExitedRef.current) {
        return;
      }
      // 4xx é rejeição de validação (OAB inválida) — deixa corrigir, sem queimar o cupom especial
      // pra sempre. Só rede/5xx cai em "consulta indisponível".
      if (isAxiosError(error) && error.response && error.response.status < 500) {
        applyNotFoundOutcome();
        return;
      }
      applyVerifyUnavailableOutcome();
    }
  };

  const onConfirmIdentity = () => {
    if (!pendingOffer) {
      return;
    }
    applyOutcome(pendingOffer);
    setPendingOffer(undefined);
  };

  const onRejectIdentity = () => {
    setPendingOffer(undefined);
    setStep('input');
  };

  const onContinueWithoutCoupon = () => {
    hasExitedRef.current = true;
    trackOabPartnershipContinuedWithoutCoupon();
    onApplyCoupon(CAA_PR_DEGUSTACAO_COUPON);
    setNotice(DEGUSTACAO_NOTICE);
    onClose();
  };

  // Só usado pelo desfecho "fora da faixa" (ver render do modal) — o único que exige
  // clique explícito em vez de auto-fechar, daí o aviso ser fixo aqui.
  const onContinueRegistration = () => {
    setNotice(NO_SPECIAL_PLAN_NOTICE);
    onClose();
  };

  return {
    step,
    oabNumber,
    setOabNumber,
    fieldError,
    isSubmitting,
    onSubmit,
    onConfirmIdentity,
    onRejectIdentity,
    // O nome já vem mascarado da API (`OabResolveOfferUseCase`, JOR-380/revisita 3) — mascarar de
    // novo aqui seria redundante e reintroduziria PII cru no bundle se alguém remover o backend.
    maskedName: pendingOffer?.name ?? '',
    inscriptionYear: pendingOffer?.inscriptionDate
      ? getInscriptionYear(pendingOffer.inscriptionDate)
      : '',
    onContinueWithoutCoupon,
    onContinueRegistration,
    previousCoupon: enteringCoupon,
    resolvedCoupon,
    notice,
  };
}
